import { CommitmentMode } from './api/commitments';

export interface SlotDef {
  /** Stable key persisted on the backend. Don't change without a data migration. */
  key: string;
  /** Group label shown above the slot in the picker (e.g. 'Newsletter'). */
  group: string;
  /** Human-readable label rendered in the visibility plan. */
  label: string;
  /** How delivery is tracked. Predefined slots have a fixed mode; only
   *  'custom' supports either. */
  mode: CommitmentMode;
  /** Optional default target — what the row gets when first added. */
  defaultTarget?: number;
}

/** Canonical slot catalog. Order here = order in the picker / on the page. */
export const SLOT_CATALOG: SlotDef[] = [
  { key: 'newsletter_featured',          group: 'Newsletter', label: 'Featured in multi-brand newsletter', mode: 'counted',   defaultTarget: 12 },
  { key: 'newsletter_dedicated',         group: 'Newsletter', label: 'Dedicated newsletter',                mode: 'counted',   defaultTarget: 4 },
  { key: 'instagram_post_story_reel',    group: 'Instagram',  label: 'Post / Story / Reel',                 mode: 'counted',   defaultTarget: 24 },
  { key: 'instagram_giveaway',           group: 'Instagram',  label: 'Giveaway',                            mode: 'counted',   defaultTarget: 2 },
  { key: 'facebook_post',                group: 'Facebook',   label: 'Post',                                mode: 'counted',   defaultTarget: 12 },
  { key: 'homepage_main_banner',         group: 'Homepage',   label: 'Main banner',                         mode: 'counted',   defaultTarget: 6 },
  { key: 'homepage_product_grid',        group: 'Homepage',   label: 'Product grid',                        mode: 'counted',   defaultTarget: 6 },
  { key: 'homepage_product_carousel',    group: 'Homepage',   label: 'Product carousel',                    mode: 'counted',   defaultTarget: 4 },
  { key: 'homepage_browse_by_room',      group: 'Homepage',   label: 'Browse-by-room carousel',             mode: 'counted',   defaultTarget: 4 },
  { key: 'homepage_brand_logo_carousel', group: 'Homepage',   label: 'Brand logo carousel (year-round)',    mode: 'always_on' },
  { key: 'website_logo_in_menu',         group: 'Website',    label: 'Logo in menu (year-round)',           mode: 'always_on' },
  { key: 'website_collection_page',      group: 'Website',    label: 'Collection page',                     mode: 'counted',   defaultTarget: 1 },
  { key: 'website_brand_page',           group: 'Website',    label: 'Brand page (updated)',                mode: 'always_on' },
];

/** Special key used when the user defines a free-form slot. */
export const CUSTOM_SLOT_KEY = 'custom';

export const getSlotDef = (key: string): SlotDef | undefined =>
  SLOT_CATALOG.find((s) => s.key === key);

export const slotLabel = (key: string, customLabel?: string): string => {
  if (key === CUSTOM_SLOT_KEY) return `Custom · ${customLabel || ''}`.trim();
  const def = getSlotDef(key);
  if (!def) return key;
  return `${def.group} · ${def.label}`;
};

/** Just the leaf label (no group prefix), useful when the group is shown
 *  separately. */
export const slotShortLabel = (key: string, customLabel?: string): string => {
  if (key === CUSTOM_SLOT_KEY) return customLabel || 'Custom';
  return getSlotDef(key)?.label || key;
};

export const slotGroup = (key: string): string => {
  if (key === CUSTOM_SLOT_KEY) return 'Custom';
  return getSlotDef(key)?.group || '';
};
